import { Observable, of } from 'rxjs'
import { filter, map, mergeAll, mergeMap, take, tap, toArray } from 'rxjs/operators'
import { root } from '../../../modules/permissions/permissions'
import { JmeleonActionsPermissionService } from '../../../modules/permissions'
import { Field } from './field'

export interface AttachmentCategory {
  attachmentCatHrid: string // HRID of attachment category catalogue entry
  displayName: string
}

export interface Dashlet {
  type: 'tile' | 'chart' | 'button' | 'log'
  chartType?: 'line' | 'bar' // for type='chart', specifies the chart type
  description?: string
  buttonLabel?: string
  actionHrid?: string
  refreshInterval?: number
}

export interface AttachmentComponent {
  type: string
  name: string
  displayName: string
  displayDescription: string
  customTypeHrid?: string
  showCollapsed?: boolean
  attachmentType?: string
  attachmentCategories?: AttachmentCategory[]
  fields?: string[]
  dashlet?: Dashlet
  backgroundColor?: string
  textColor?: string
  gridHeight?: number
  gridWidth?: number
  height?: string
  condition?: string
}

export class EntityAttachment {
  width?: string // Width including any uom permitted by CSS, e.g. 500px, 500rem, 100%, .... Usually it will be given in %.
  minWidth?: string // Defines the minimum width of an element. If element is smaller it will be wrapped.
  maxWidth?: string // Defines the maximum width of an element. Element will not exceed this width even though more space is available. This allows next items to fill the gap.
  elements: [
    {
      tabView?: AttachmentComponent[]
      panel?: AttachmentComponent[]
      box?: AttachmentComponent[]
      autoGrid?: AttachmentComponent[]
    }
  ]
}

export class EntityFilterTile {
  id: string // hrid of search expression
  header: string // display name
  description: string // info text with details
  filterExpression: string // python expression
  softFilterExpression: string // python expression
  count?: number // return value of api call /entities/count

  constructor(
    id: string,
    header: string,
    description: string,
    filterExpression: string,
    softFilterExpression: string = '',
    count: number = 0
  ) {
    this.id = id
    this.header = header
    this.description = description
    this.filterExpression = filterExpression
    this.softFilterExpression = softFilterExpression
    this.count = count
  }
}

export class EntityConfiguration {
  constructor() {}

  type: string
  viewType: string // 'singlePage' | 'multiPage' ;
  groupType: string
  displayName: string
  singleDisplayName: string
  rowsPerPage: number
  onAttachPost: {
    condition: string
    action: string
  }
  entityAttachments: EntityAttachment[]
  entityFilterTiles: EntityFilterTile[]
  fields: Field[]
  footerFields: Field[]
  minWidth: number
  actions: Object
  globalActions: any[]
  customCreation: {
    creationMode: string
    creationAction: {
      action: string
      condition: string
    }
  }
  customUpdate: {
    updateMode: string
    updateAction: {
      action: string
      condition: string
    }
  }
  selectEntityAfterCreation = true
  refreshOnModify: boolean

  orderedColumn: string

  softFilter: string

  createDialogHeaderText: string
  createDescription: string
  createButtonText: string
  createSuccessMessage: string

  activateFilterViaDoubleClick: boolean

  updateDialogHeaderText: string
  updateDescription: string
  updateButtonText: string
  updateSuccessMessage: string

  defaultStateMachine: string

  private editableFields: Field[]
  private visibleFields: Field[]
  private creationFields: Field[]

  private setEditableFields$(japs: JmeleonActionsPermissionService): Observable<Field[]> {
    this.editableFields = []
    const editableFields$ = of(this.fields).pipe(
      mergeAll(),
      filter((field) => field.showOnUpdate === true && field.editable === true),
      // for each field check permissions and set this.editableFields
      mergeMap((field) => {
        const editPermissions = [root.dto.$dtoType.write, root.dto.$dtoType.$dtoField.write]
        return japs.userHasPermissionForActions(editPermissions, { $dtoType: this.type, $dtoField: field.field }).pipe(
          take(1),
          // filter fields depending on permissions
          filter(
            (permissions) => permissions[root.dto.$dtoType.write] && permissions[root.dto.$dtoType.$dtoField.write]
          ),
          tap((_) => this.editableFields.push(field)),
          map((_) => field)
        )
      }),
      toArray()
    )
    return editableFields$
  }

  getEditableFields$(japs: JmeleonActionsPermissionService): Observable<Field[]> {
    if (this.editableFields === undefined) {
      return this.setEditableFields$(japs)
    } else {
      return of(this.editableFields)
    }
  }

  private setVisibleFields$(japs: JmeleonActionsPermissionService): Observable<Field[]> {
    this.visibleFields = []
    const visibleFields$ = of(this.fields).pipe(
      mergeAll(),
      filter((field) => field.visible === true),
      // for each field check permissions and set this.editableFields
      mergeMap((field) => {
        const readPermissions = [root.dto.$dtoType.read, root.dto.$dtoType.$dtoField.read]
        return japs.userHasPermissionForActions(readPermissions, { $dtoType: this.type, $dtoField: field.field }).pipe(
          take(1),
          // filter fields depending on permissions
          filter((permissions) => permissions[root.dto.$dtoType.read] && permissions[root.dto.$dtoType.$dtoField.read]),
          tap((_) => this.visibleFields.push(field)),
          map((_) => field)
        )
      }),
      toArray()
    )
    return visibleFields$
  }

  getVisibleFields$(japs: JmeleonActionsPermissionService): Observable<Field[]> {
    if (this.visibleFields === undefined) {
      return this.setVisibleFields$(japs)
    } else {
      return of(this.visibleFields)
    }
  }

  private setCreationFields$(japs: JmeleonActionsPermissionService): Observable<Field[]> {
    this.creationFields = []
    return japs.userHasPermissionForAction(root.dto.$dtoType.create, { $dtoType: this.type }).pipe(
      take(1),
      filter((createAllowed) => createAllowed === true),
      map((_) => this.fields.filter((field) => field.showOnCreate === true || field.mandatory === true)),
      tap((fields) => (this.creationFields = fields))
    )
  }

  getCreationFields$(japs: JmeleonActionsPermissionService): Observable<Field[]> {
    if (this.creationFields === undefined) {
      return this.setCreationFields$(japs)
    } else {
      return of(this.creationFields)
    }
  }
}
